import { NS, STYLES, REFERENCES, EVENTS, CLICK, KEYDOWN, DOT } from "../constants";
import type { IChatOptions, IMessage } from "../models";
import type { IKendoJQuery } from "../../core";

declare const $: any;

export interface IEventWiringContext {
    options: IChatOptions;
    wrapper: IKendoJQuery;
    view: {
        bind: (event: string, handler: (...args: any[]) => void) => any;
    };
    messageBox: {
        bind: (event: string, handler: (...args: any[]) => void) => any;
    };
    messageContextMenu: {
        bind: (event: string, handler: (...args: any[]) => void) => any;
    };
    value: (newValue?: string) => string | void;
    postMessage: (message: string | Partial<IMessage>) => IMessage;
    clearReplyState: () => void;
    commandExecute: (e: any) => void;
    contextMenuExecute: (e: any) => void;
    messageContextMenuOpen: (e: any) => void;
    dataItem: (message: IKendoJQuery) => IMessage | null;
    scrollToMessage: (uid: string) => boolean;
    clearPinnedMessage: () => void;
    getPinnedMessage: () => IMessage | null;
    getMessageById: (id: string) => IMessage | null;
    trigger: (event: string, args?: any) => void;
    accessibility: {
        updateExpandableIndicatorAria: (indicator: any, isExpanded: boolean) => void;
        handleKeyDown: (e: any, messageBox: any) => void;
        setupAriaAttributes: () => void;
    };
    messageBoxRef: any;
}

export class EventWiring {
    private context: IEventWiringContext;

    constructor(context: IEventWiringContext) {
        this.context = context;
    }

    attach(): void {
        this._attachViewEvents();
        this._attachMessageBoxEvents();
        this._attachContextMenuEvents();
        this._attachWrapperDomEvents();
    }

    private _attachViewEvents(): void {
        const ctx = this.context;

        ctx.view
            .bind("suggestedActionClick", (args: any) => {
                const options = ctx.options;
                if (options.suggestionsBehavior === "insert") {
                    ctx.value(args.text);
                    ctx.trigger(EVENTS.suggestionClick, { text: args.text });
                } else {
                    const message = { text: args.text, files: [] };
                    ctx.trigger(EVENTS.sendMessage, { message });
                    ctx.postMessage(message);
                }
            })
            .bind("messageToolbarExecute", (args: any) => {
                const message = ctx.dataItem(args.message);
                ctx.commandExecute({ type: args.type, message: args.message });
                ctx.trigger(EVENTS.toolbarAction, { type: args.type, message });
            })
            .bind("fileMenuExecute", (args: any) => {
                ctx.commandExecute(args);
            })
            .bind("downloadAllFiles", (args: any) => {
                const message = ctx.dataItem(args.messageElement);
                ctx.trigger(EVENTS.download, { files: message?.files, message });
            })
            .bind("expandableToggle", (args: any) => {
                ctx.accessibility.updateExpandableIndicatorAria(args.indicator, args.isExpanded);
            });
    }

    private _attachMessageBoxEvents(): void {
        const ctx = this.context;

        ctx.messageBox
            .bind("input", (args: any) => {
                ctx.trigger(EVENTS.input, { value: args.value });
            })
            .bind("sendMessage", (args: any) => {
                const generating = args.generating;

                if (generating) {
                    ctx.trigger(EVENTS.sendMessage, { generating });
                } else {
                    const message = { text: args.text || args.message?.text, files: args.files || args.message?.files || [] };
                    ctx.trigger(EVENTS.sendMessage, { message });
                    ctx.postMessage(message);
                    ctx.clearReplyState();
                }
            })
            .bind("suggestionClick", (args: any) => {
                ctx.trigger(EVENTS.suggestionClick, { text: args.text });
                if (args.behavior !== "insert") {
                    const message = { text: args.text, files: [] };
                    ctx.trigger(EVENTS.sendMessage, { message });
                    ctx.postMessage(message);
                }
            })
            .bind("fileSelect", (args: any) => {
                ctx.trigger(EVENTS.fileSelect, { files: args.files });
            })
            .bind("fileRemove", (args: any) => {
                ctx.trigger(EVENTS.fileRemove, { file: args.file, files: args.files });
            })
            .bind("replyMessageCloseButtonClick", () => {
                ctx.clearReplyState();
            });
    }

    private _attachContextMenuEvents(): void {
        const ctx = this.context;

        ctx.messageContextMenu
            .bind("execute", ctx.contextMenuExecute.bind(ctx))
            .bind("open", ctx.messageContextMenuOpen.bind(ctx));
    }

    private _attachWrapperDomEvents(): void {
        const ctx = this.context;

        ctx.wrapper.on(CLICK + NS, `[${REFERENCES.pinnedMessageCloseButton}]`, () => {
            ctx.trigger(EVENTS.unpin, { message: ctx.getPinnedMessage() });
            ctx.clearPinnedMessage();
        });

        ctx.wrapper.on(CLICK + NS, `[${REFERENCES.messageReferencePinWrapper}]`, (e: any) => {
            if ($(e.target).closest(`[${REFERENCES.pinnedMessageCloseButton}]`).length) {
                return;
            }

            if ($(e.target).is("a")) {
                return;
            } else {
                e.preventDefault();
                e.stopPropagation();
            }

            const pinnedMessage = ctx.getPinnedMessage();

            if (pinnedMessage && pinnedMessage.uid) {
                ctx.scrollToMessage(pinnedMessage.uid);
            }
        });

        ctx.wrapper.on(CLICK + NS, `[${REFERENCES.messageReferenceReplyWrapper}]`, (e: any) => {
            if ($(e.target).is("a")) {
                return;
            } else {
                e.preventDefault();
                e.stopPropagation();
            }

            const messageElement = $(e.currentTarget).closest(DOT + STYLES.message);
            const currentMessage = ctx.dataItem(messageElement);

            if (currentMessage && currentMessage.replyToId) {
                const originalMessage = ctx.getMessageById(currentMessage.replyToId);

                if (originalMessage && originalMessage.uid) {
                    ctx.scrollToMessage(originalMessage.uid);
                }
            }
        });

        ctx.wrapper.on(KEYDOWN + NS, (e: any) => {
            ctx.accessibility.handleKeyDown(e as any, ctx.messageBoxRef);
        });

        ctx.wrapper.on(`focus${NS}`, `.${STYLES.bubble}`, (e: any) => {
            const bubbleElement = $(e.target);

            if (bubbleElement.find(`.${STYLES.typingIndicator}`).length > 0) {
                return;
            }

            if (bubbleElement.attr("data-keyboard-focus") === "true") {
                const messageElement = bubbleElement.closest(`.${STYLES.message}`);
                const message = ctx.dataItem(messageElement);

                if (message && message.uid) {
                    ctx.scrollToMessage(message.uid);
                }
            }
        });
    }
}
